import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Trash2,
  Loader2,
  Cpu,
  MemoryStick,
  HardDrive,
  Pencil,
  RefreshCw,
  AlertTriangle,
  Package,
} from 'lucide-react';
import SearchableSelect from '@/components/common/SearchableSelect';
import {
  apiMessage,
  architectureLabel,
  hetznerCost,
  SUPPORTED_ARCHITECTURE,
} from '@/lib/pricing';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HetznerApiPrice {
  location: string;
  price_hourly_net: string | null;
  price_hourly_gross: string | null;
  price_monthly_net: string | null;
  price_monthly_gross: string | null;
}

interface ApiServerType {
  name: string;
  description: string;
  cores: number;
  memory: number;
  disk: number;
  cpu_type: string;
  architecture: string;
  prices: HetznerApiPrice[];
}

interface ApiLocation {
  name: string;
  description: string;
  city: string;
  country: string;
  network_zone: string;
}

interface CatalogData {
  server_types: ApiServerType[];
  locations: ApiLocation[];
}

interface PriceEntry {
  id: string;
  tier: string;
  plan: string | null;
  region: string;
  currency: string;
  effective_date: string;
  monthly_price: string;
  set_by: string;
  set_at: string | null;
  notes: string | null;
}

type OfferingChange = {
  id: string;
  changed_at: string;
  changed_by_email: string;
  server_type: string;
  display_name: string;
  action: 'created' | 'updated' | 'deleted' | 'price_added' | 'price_removed';
  changes: Record<string, any>;
};

const CHANGE_LABEL: Record<string, string> = {
  display_name: 'Display name',
  description: 'Description',
  hetzner_location: 'Location',
  vcpus: 'vCPUs',
  memory_gb: 'RAM (GB)',
  disk_gb: 'Disk (GB)',
  is_active: 'Active',
  trial_eligible: 'Available on the free trial',
  sort_order: 'Sort order',
};

function describeChange(entry: OfferingChange): string {
  const name = entry.display_name ? `${entry.display_name} (${entry.server_type})` : entry.server_type;
  switch (entry.action) {
    case 'created': return `${name} created`;
    case 'deleted': return `${name} deleted`;
    case 'price_added': return `${name}: price ${entry.changes.monthly_price} ${entry.changes.currency}/mo${entry.changes.region_name ? ` in ${entry.changes.region_name}` : ''} from ${new Date(entry.changes.effective_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}${entry.changes.notes ? ` · ${entry.changes.notes}` : ''}`;
    case 'price_removed': return `${name}: price ${entry.changes.monthly_price} ${entry.changes.currency}/mo${entry.changes.region_name ? ` in ${entry.changes.region_name}` : ''} (from ${entry.changes.effective_date}) removed`;
    default: return `${name} updated`;
  }
}

function OfferingChangeLog({ entries }: { entries: OfferingChange[] }) {
  if (entries.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No changes recorded yet.</p>;
  }
  return (
    <ol className="space-y-3" data-testid="offering-change-list">
      {entries.map((entry) => (
        <li key={entry.id} className="rounded-md border p-3 text-sm" data-testid={`offering-change-${entry.id}`}>
          <p>
            <span className="font-medium">{new Date(entry.changed_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
            <span className="text-muted-foreground"> · {entry.changed_by_email || 'a global admin'} · </span>
            {describeChange(entry)}
          </p>
          {entry.action === 'updated' && (
            <ul className="mt-2 space-y-1">
              {Object.entries(entry.changes).map(([field, change]) => (
                <li key={field} className="flex flex-wrap gap-x-2">
                  <span className="w-44 shrink-0 text-muted-foreground">{CHANGE_LABEL[field] ?? field}</span>
                  <span className="line-through decoration-muted-foreground/60 text-muted-foreground">{String(change.from ?? '') || '—'}</span>
                  <span>→ {String(change.to ?? '') || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

interface ServerConfig {
  id: string;
  hetzner_server_type: string;
  hetzner_location: string | null;
  display_name: string;
  description: string | null;
  vcpus: number;
  memory_gb: number;
  disk_gb: number;
  is_active: boolean;
  trial_eligible: boolean;
  sort_order: number;
  prices: Record<string, Record<string, PriceEntry[]>>;
  created_at: string;
  updated_at: string;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const QK_CONFIGS = ['hetzner-configs'];
const QK_CATALOG = ['hetzner-catalog'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export default function ServerConfigsPage() {
  const queryClient = useQueryClient();

  // ─── Fetch offerings (DB) ──────────────────────────────────────────
  const changeLog = useQuery({
    queryKey: ['hetzner-offering-changes'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-configs/changes/')).data as OfferingChange[];
    },
  });
  const {
    data: configs,
    isLoading: configsLoading,
  } = useQuery<ServerConfig[]>({
    queryKey: QK_CONFIGS,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-configs/');
      return res.data;
    },
  });

  // ─── Fetch catalog (Hetzner API data for dropdowns / lookups) ──────
  const {
    data: catalog,
    error: catalogError,
  } = useQuery<CatalogData>({
    queryKey: QK_CATALOG,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-catalog/');
      return res.data;
    },
  });

  const isApiKeyMissing =
    catalogError && (catalogError as any)?.response?.status === 503;

  // Build lookup maps from catalog
  const serverTypeMap = useMemo(() => {
    const map = new Map<string, ApiServerType>();
    catalog?.server_types.forEach((st) => map.set(st.name, st));
    return map;
  }, [catalog]);

  // ─── Refresh catalog ───────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/hetzner-catalog/?refresh=true');
      queryClient.setQueryData(QK_CATALOG, res.data);
      toast.success('Catalog refreshed from Hetzner API');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ─── Toggle active mutation ────────────────────────────────────────
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/hetzner-configs/${id}/`, { is_active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
    },
    onError: (err: any) => {
      toast.error(apiMessage(err));
    },
  });

  const toggleTrialMutation = useMutation({
    mutationFn: async ({ id, trial_eligible }: { id: string; trial_eligible: boolean }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/hetzner-configs/${id}/`, { trial_eligible });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
    },
    onError: (err: any) => {
      toast.error(apiMessage(err));
    },
  });

  // ─── Delete offering mutation ──────────────────────────────────────
  const deleteConfigMutation = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/hetzner-configs/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
      toast.success('Offering deleted');
    },
    onError: (err: any) => {
      toast.error(apiMessage(err));
    },
  });

  // ─── Add Offering dialog ──────────────────────────────────────────
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    server_type: '',
    display_name: '',
  });

  const selectedApiType = serverTypeMap.get(addForm.server_type);
  const selectedCost = hetznerCost(selectedApiType, null);

  const createOfferingMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post('/global-admin/hetzner-configs/', {
        hetzner_server_type: addForm.server_type,
        display_name: addForm.display_name,
        vcpus: selectedApiType?.cores ?? 0,
        memory_gb: selectedApiType ? Math.round(selectedApiType.memory) : 0,
        disk_gb: selectedApiType?.disk ?? 0,
        is_active: true,
        sort_order: 0,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
      setShowAddDialog(false);
      toast.success('Offering created. Give it a price on the Pricing page before it can be sold.');
    },
    onError: (err: any) => {
      toast.error(apiMessage(err));
    },
  });

  const openAddDialog = () => {
    setAddForm({ server_type: '', display_name: '' });
    setShowAddDialog(true);
  };

  // Auto-suggest display name when server type changes
  const handleServerTypeChange = (value: string) => {
    const apiType = serverTypeMap.get(value);
    setAddForm((prev) => ({
      ...prev,
      server_type: value,
      display_name: apiType?.description || value,
    }));
  };

  // ─── Edit Offering dialog ─────────────────────────────────────────
  const [editingConfig, setEditingConfig] = useState<ServerConfig | null>(null);
  const [editForm, setEditForm] = useState({
    display_name: '',
    description: '',
    sort_order: '0',
    is_active: true,
    trial_eligible: false,
  });

  const editConfigMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      display_name: string;
      description: string;
      sort_order: number;
      is_active: boolean;
      trial_eligible: boolean;
    }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put(`/global-admin/hetzner-configs/${data.id}/`, {
        display_name: data.display_name,
        description: data.description,
        sort_order: data.sort_order,
        is_active: data.is_active,
        trial_eligible: data.trial_eligible,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_CONFIGS });
      queryClient.invalidateQueries({ queryKey: ['hetzner-offering-changes'] });
      setEditingConfig(null);
      toast.success('Offering updated');
    },
    onError: (err: any) => {
      toast.error(apiMessage(err));
    },
  });

  const openEditDialog = (config: ServerConfig) => {
    setEditingConfig(config);
    setEditForm({
      display_name: config.display_name,
      description: config.description || '',
      sort_order: String(config.sort_order),
      is_active: config.is_active,
      trial_eligible: config.trial_eligible,
    });
  };

  // ─── Loading state ────────────────────────────────────────────────
  if (configsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Server Offerings</h2>
          <p className="text-muted-foreground">
            Manage Hetzner Cloud server offerings with custom pricing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh API Data
          </Button>
          <Button onClick={openAddDialog} disabled={!catalog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Offering
          </Button>
        </div>
      </div>

      {isApiKeyMissing && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-3">
            <AlertTriangle className="h-10 w-10 text-amber-500" />
            <div className="text-center space-y-1">
              <h3 className="font-semibold">Hetzner API Key Not Configured</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                API data (server types, locations, Hetzner pricing) is unavailable.
                Existing offerings still work. Add your Hetzner API key in <strong>API Keys</strong> settings.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Offerings table ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Offerings
            <Badge variant="secondary" className="ml-1">
              {configs?.filter((c) => c.is_active).length ?? 0} active
            </Badge>
          </CardTitle>
          <CardDescription>
            The sizes on offer and what each costs us. What customers pay
            for them is set on the Pricing page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Active</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Server Type</TableHead>
                <TableHead className="text-right">vCPUs</TableHead>
                <TableHead className="text-right">RAM</TableHead>
                <TableHead className="text-right">Disk</TableHead>
                <TableHead className="text-right">Our cost</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!configs || configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No offerings yet. Click "Add Offering" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => {
                  const apiType = serverTypeMap.get(config.hetzner_server_type);
                  const cost = hetznerCost(apiType, config.hetzner_location);
                  const isToggling =
                    toggleActiveMutation.isPending &&
                    (toggleActiveMutation.variables as any)?.id === config.id;

                  return (
                    <TableRow key={config.id} className={config.is_active ? '' : 'opacity-60'}>
                      <TableCell>
                        {isToggling ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Switch
                            checked={config.is_active}
                            onCheckedChange={(checked) =>
                              toggleActiveMutation.mutate({ id: config.id, is_active: checked })
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{config.display_name}</div>
                        {config.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {config.description}
                          </div>
                        )}
                        <label className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={config.trial_eligible}
                            onChange={(e) =>
                              toggleTrialMutation.mutate({
                                id: config.id,
                                trial_eligible: e.target.checked,
                              })
                            }
                            data-testid={`trial-eligible-${config.id}`}
                          />
                          Available on the free trial
                        </label>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{config.hetzner_server_type}</div>
                        {apiType && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            <Badge
                              variant={apiType.cpu_type === 'dedicated' ? 'default' : 'outline'}
                              className="text-xs"
                            >
                              {apiType.cpu_type}
                            </Badge>
                            <Badge
                              variant={apiType.architecture === SUPPORTED_ARCHITECTURE ? 'outline' : 'destructive'}
                              className="text-xs"
                              data-testid={`config-arch-${config.hetzner_server_type}`}
                            >
                              {architectureLabel(apiType.architecture)}
                            </Badge>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <Cpu className="h-3 w-3 text-muted-foreground" />
                          {config.vcpus}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <MemoryStick className="h-3 w-3 text-muted-foreground" />
                          {config.memory_gb} GB
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1 text-sm">
                          <HardDrive className="h-3 w-3 text-muted-foreground" />
                          {config.disk_gb} GB
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm" data-testid={`config-cost-${config.hetzner_server_type}`}>
                        {cost != null
                          ? `${cost.toFixed(2)} EUR/mo`
                          : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Edit offering"
                            onClick={() => openEditDialog(config)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            title="Delete offering"
                            onClick={() => {
                              if (confirm(`Delete offering "${config.display_name}"?`)) {
                                deleteConfigMutation.mutate(config.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card data-testid="offering-change-log">
        <CardHeader>
          <CardTitle>Change log</CardTitle>
          <CardDescription>Every offering created, edited or deleted, and every price added or removed, with who did it.</CardDescription>
        </CardHeader>
        <CardContent>
          {changeLog.isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <OfferingChangeLog entries={changeLog.data ?? []} />
          )}
        </CardContent>
      </Card>

      {/* ─── Add Offering Dialog ──────────────────────────────────────── */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Offering</DialogTitle>
            <DialogDescription>
              Create a new server offering by selecting a Hetzner server type.
            </DialogDescription>
          </DialogHeader>
          {!catalog ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Server Type</Label>
                <SearchableSelect
                  value={addForm.server_type}
                  onChange={handleServerTypeChange}
                  placeholder="Select a server type..."
                  searchPlaceholder="Search server types…"
                  options={catalog.server_types.map((st) => {
                    const cost = hetznerCost(st, null);
                    const supported = st.architecture === SUPPORTED_ARCHITECTURE;
                    return {
                      value: st.name,
                      label: st.name,
                      keywords: architectureLabel(st.architecture),
                      hint: [
                        architectureLabel(st.architecture),
                        `${st.cores}v / ${st.memory}GB / ${st.disk}GB`,
                        cost != null ? `${cost.toFixed(2)} EUR/mo` : null,
                        supported ? null : 'not supported',
                      ].filter(Boolean).join(' · '),
                      disabled: !supported,
                    };
                  })}
                  testId="server-type-select"
                />
              </div>

              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  placeholder="e.g. Standard 8GB"
                  value={addForm.display_name}
                  onChange={(e) => setAddForm((f) => ({ ...f, display_name: e.target.value }))}
                />
              </div>

              {selectedApiType && (
                <div className="rounded-md border p-3 space-y-1 text-sm">
                  <div className="font-medium text-muted-foreground">Auto-populated specs:</div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Cpu className="h-3.5 w-3.5" /> {selectedApiType.cores} vCPUs · {architectureLabel(selectedApiType.architecture)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MemoryStick className="h-3.5 w-3.5" /> {selectedApiType.memory} GB RAM
                    </span>
                    <span className="flex items-center gap-1">
                      <HardDrive className="h-3.5 w-3.5" /> {selectedApiType.disk} GB Disk
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-testid="add-offering-cost">
                    <span className="font-medium text-muted-foreground">Our cost</span>
                    <span className="font-mono">
                      {selectedCost != null ? `${selectedCost.toFixed(2)} EUR/mo` : 'unknown'}
                    </span>
                  </div>
                </div>
              )}

              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                Give it a price on the Pricing page before it can be sold.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => createOfferingMutation.mutate()}
              disabled={
                createOfferingMutation.isPending ||
                !addForm.server_type ||
                !addForm.display_name
              }
            >
              {createOfferingMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Offering
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Offering Dialog ─────────────────────────────────────── */}
      <Dialog open={editingConfig !== null} onOpenChange={(open) => !open && setEditingConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Offering</DialogTitle>
            <DialogDescription>
              Edit display settings for this offering. Server type cannot be changed.
            </DialogDescription>
          </DialogHeader>
          {editingConfig && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Server Type:</span>
                  <span className="font-mono font-medium">{editingConfig.hetzner_server_type}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Optional description..."
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editForm.sort_order}
                    onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="edit-is-active"
                    checked={editForm.is_active}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                  />
                  <Label htmlFor="edit-is-active">Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-trial-eligible"
                    checked={editForm.trial_eligible}
                    onCheckedChange={(checked) =>
                      setEditForm({ ...editForm, trial_eligible: checked })
                    }
                  />
                  <Label htmlFor="edit-trial-eligible">Available on the free trial</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!editingConfig) return;
                editConfigMutation.mutate({
                  id: editingConfig.id,
                  display_name: editForm.display_name,
                  description: editForm.description,
                  sort_order: parseInt(editForm.sort_order) || 0,
                  is_active: editForm.is_active,
                  trial_eligible: editForm.trial_eligible,
                });
              }}
              disabled={editConfigMutation.isPending}
            >
              {editConfigMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
